import { apiRequest } from "@/lib/api";
import { type ApiTicketStatus } from "@/lib/tickets";
import { type ApprovalDeliveryMethod } from "@/lib/tickets";

export type ApprovalListItem = {
  id: string;
  ticketId: string;
  ticketNumber: string;
  title: string;
  recipientName: string;
  recipientEmail: string;
  recipientPhone?: string | null;
  deliveryMethod?: ApprovalDeliveryMethod;
  project?: { id: string; name: string } | null;
  client?: { id: string; name: string } | null;
  sentAt?: string | null;
  lastOpenedAt?: string | null;
  lastReminderAt?: string | null;
  reminderCount: number;
  agingDays: number;
  value: number;
  status: ApiTicketStatus;
};

export type ApprovalDetail = ApprovalListItem & {
  ccEmails: string[];
  approvalLink?: string;
  decision?: string | null;
  decisionComment?: string | null;
  decidedAt?: string | null;
  events: Array<{
    id: string;
    eventType: string;
    actorName?: string | null;
    actorEmail?: string | null;
    payload?: unknown;
    happenedAt: string;
  }>;
};

export type ApprovalFilters = {
  search?: string;
  projectId?: string;
  clientId?: string;
  statuses?: ApiTicketStatus[];
  agingBucket?: "0-12h" | "12-36h" | "36-72h" | "72h+" | "0-3" | "4-7" | "8-14" | "15+";
  neverOpened?: boolean;
  startDate?: string;
  endDate?: string;
};

export function fetchApprovals(filters: ApprovalFilters = {}) {
  const query = new URLSearchParams();
  if (filters.search?.trim()) query.set("search", filters.search.trim());
  if (filters.projectId) query.set("projectId", filters.projectId);
  if (filters.clientId) query.set("clientId", filters.clientId);
  if (filters.agingBucket) query.set("agingBucket", filters.agingBucket);
  if (filters.neverOpened) query.set("neverOpened", "true");
  if (filters.startDate) query.set("startDate", filters.startDate);
  if (filters.endDate) query.set("endDate", filters.endDate);
  if (filters.statuses?.length) query.set("statuses", filters.statuses.join(","));
  return apiRequest<ApprovalListItem[]>(`/approvals?${query.toString()}`, { method: "GET" });
}

export function fetchApprovalById(id: string) {
  return apiRequest<ApprovalDetail>(`/approvals/${id}`, { method: "GET" });
}

export function sendApprovalReminder(
  id: string,
  payload?: { smsConsentConfirmed?: boolean },
) {
  return apiRequest<{ id: string; approvalRequestId: string; status: string }>(`/approvals/${id}/reminder-now`, {
    method: "POST",
    body: payload,
  }).then((result) => {
    window.dispatchEvent(new Event("workscope:notifications-refresh"));
    return result;
  });
}

export function resendApprovalLink(
  id: string,
  payload?: { smsConsentConfirmed?: boolean },
) {
  return apiRequest<{ success: boolean }>(`/approvals/${id}/resend-link`, {
    method: "POST",
    body: payload,
  }).then((result) => {
    window.dispatchEvent(new Event("workscope:notifications-refresh"));
    return result;
  });
}

export function changeApprovalRecipient(
  id: string,
  payload: { recipientName?: string; recipientEmail: string; ccEmails?: string[] },
) {
  return apiRequest<{
    id: string;
    recipientName: string;
    recipientEmail: string;
    ccEmails: string[];
  }>(`/approvals/${id}/recipient`, {
    method: "PATCH",
    body: payload,
  }).then((result) => {
    window.dispatchEvent(new Event("workscope:notifications-refresh"));
    return result;
  });
}
